<template>
  <v-container fluid class="auth-wrapper pa-0">
    <v-row no-gutters class="fill-height min-vh-100">
      <!-- Left Side: Visual Pane -->
      <v-col cols="12" md="6" class="visual-pane d-none d-md-flex align-center justify-center">
        <div class="visual-content text-center pa-12">
          <v-img
            max-width="260"
            class="mx-auto mb-10"
            src="@/assets/runflow_logo.png"
          ></v-img>
          <div class="brand-message">
            <h1 class="text-h3 font-weight-bold white--text mb-6 raleway-font tracking-tight">
              Nueva <span class="accent-text">Seguridad</span>.
            </h1>
            <p class="text-h6 white--text opacity-70 lato-font font-weight-light lh-relaxed">
              Establece una contraseña robusta para proteger <br>tu flujo de trabajo industrial.
            </p>
          </div>
        </div>
      </v-col>

      <!-- Right Side: Change Password Form -->
      <v-col cols="12" md="6" class="form-pane d-flex align-center justify-center">
        <div class="auth-card-container pa-8 pa-sm-16">
          
          <!-- Loading state during validation -->
          <div v-if="loading && !sent" class="text-center">
            <v-progress-circular indeterminate color="#35BDC8" size="64" width="6" class="mb-6"></v-progress-circular>
            <p class="body-1 slate-text font-weight-bold lato-font">Validando tu solicitud...</p>
          </div>

          <!-- Main Form: Set New Password -->
          <div v-else-if="!sent && validated" class="text-center">
            <div v-if="logo" class="company-logo-login-container mb-8 pa-4 d-inline-block">
              <v-img :src="logo" contain max-height="80" max-width="180" class="mx-auto"></v-img>
            </div>
            <div class="mb-10 text-left">
              <h2 class="text-h4 font-weight-bold slate-text raleway-font mb-2">Cambio de Contraseña</h2>
              <p class="grey--text text--darken-1 lato-font font-weight-medium">Por favor ingresa tu nueva clave de acceso.</p>
            </div>

            <v-form @submit.prevent="changePassword">
              <div class="input-group mb-6">
                <label class="input-label mb-2 d-block slate-text font-weight-bold text-uppercase caption tracking-wider">Nueva Contraseña</label>
                <v-text-field
                  v-model="password1"
                  placeholder="Mínimo 8 caracteres"
                  outlined
                  flat
                  dense
                  class="modern-input-field"
                  prepend-inner-icon="mdi-lock-outline"
                  :append-icon="showPassword1 ? 'mdi-eye-outline' : 'mdi-eye-off-outline'"
                  @click:append="showPassword1 = !showPassword1"
                  :type="showPassword1 ? 'text' : 'password'"
                  hide-details="auto"
                  color="#35BDC8"
                ></v-text-field>
              </div>

              <div class="input-group mb-8">
                <label class="input-label mb-2 d-block slate-text font-weight-bold text-uppercase caption tracking-wider">Confirmar Contraseña</label>
                <v-text-field
                  v-model="password2"
                  placeholder="Repite tu contraseña"
                  outlined
                  flat
                  dense
                  class="modern-input-field"
                  prepend-inner-icon="mdi-lock-check-outline"
                  :append-icon="showPassword2 ? 'mdi-eye-outline' : 'mdi-eye-off-outline'"
                  @click:append="showPassword2 = !showPassword2"
                  :type="showPassword2 ? 'text' : 'password'"
                  hide-details="auto"
                  color="#35BDC8"
                ></v-text-field>
              </div>

              <v-btn 
                color="#35BDC8" 
                large 
                block 
                depressed
                type="submit" 
                :loading="loading"
                :disabled="loading"
                class="h-52 rounded-md white--text font-weight-bold text-uppercase tracking-widest login-btn-enterprise"
              >
                Actualizar Contraseña
                <template v-slot:loader>
                  <v-progress-circular indeterminate size="22" width="2" color="white"></v-progress-circular>
                </template>
              </v-btn>
            </v-form>
          </div>

          <!-- Success State -->
          <div v-else-if="sent && success" class="text-center">
            <v-avatar size="80" color="rgba(76, 175, 80, 0.1)" class="mb-8">
              <v-icon size="36" color="#4CAF50">fas fa-check-circle</v-icon>
            </v-avatar>
            <h2 class="text-h4 font-weight-bold slate-text raleway-font mb-4">¡Cambio Exitoso!</h2>
            <p class="grey--text text--darken-1 body-1 lato-font mb-10">Tu contraseña ha sido actualizada correctamente. Ahora puedes iniciar sesión.</p>
            <v-btn to="/auth/login" color="#35BDC8" block large depressed class="h-52 rounded-md white--text font-weight-bold text-uppercase tracking-widest">
              Ir al Login
            </v-btn>
          </div>

          <!-- Error State -->
          <div v-else-if="error" class="text-center">
            <v-avatar size="80" color="rgba(244, 67, 54, 0.1)" class="mb-8">
              <v-icon size="36" color="#F44336">fas fa-shield-alt</v-icon>
            </v-avatar>
            <h2 class="text-h4 font-weight-bold error--text raleway-font mb-4">Error de Seguridad</h2>
            <p class="grey--text text--darken-1 body-1 lato-font mb-10">{{ message || 'La solicitud no es válida o ha expirado.' }}</p>
            <v-btn v-if="!validated" to="/auth/password-reset/email" color="#35BDC8" block large depressed class="h-52 rounded-md white--text font-weight-bold text-uppercase tracking-widest mb-4">
              Solicitar Nuevo Enlace
            </v-btn>
            <v-btn @click="retry" color="#1A1F2E" block large depressed class="h-52 rounded-md white--text font-weight-bold text-uppercase tracking-widest">
              Intentar Nuevamente
            </v-btn>
          </div>

          <div class="mt-16 pt-8 text-center border-top-light">
            <p class="caption grey--text text--darken-1 mb-0 lato-font">
              &copy; {{ new Date().getFullYear() }} <strong>Runflow Evolution 9</strong>. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { passwordResetValidate, passwordReset, getLogo } from '@/api/auth'
import notifications from '@/utils/notifications'

export default {
  name: 'PasswordResetRequest',
  data: () => ({
    loading: false,
    password1: '',
    password2: '',
    showPassword1: false,
    showPassword2: false,
    sent: false,
    success: false,
    error: false,
    validated: false,
    message: '',
    token: '',
    logo: ''
  }),
  async created() {
    try {
      this.logo = await getLogo()
    } catch (e) {
      console.error(e)
    }
    
    if (this.$route.params.token) {
      this.token = this.$route.params.token
      this.loading = true
      try {
        await passwordResetValidate(this.token)
        this.validated = true
      } catch (e) {
        this.error = true
        this.message = 'El enlace de recuperación no es válido o ya ha sido utilizado.'
      } finally {
        this.loading = false
      }
    } else {
      this.error = true
      this.message = 'Falta el token de seguridad para realizar esta acción.'
    }
  },
  methods: {
    async changePassword() {
      if (!this.password1 || !this.password2) return
      
      if (this.password1 !== this.password2) {
        notifications.showError("Las contraseñas no coinciden")
        return
      }

      this.loading = true
      try {
        await passwordReset({ token: this.token, password: this.password1 })
        this.success = true
        this.sent = true
        notifications.showSuccess('Contraseña actualizada exitosamente')
      } catch (e) {
        console.error(e)
        notifications.showError('Error al actualizar: ' + (e.response?.data?.password?.[0] || 'Intente nuevamente'))
        this.error = true
        this.sent = true // Show error state
      } finally {
        this.loading = false
      }
    },
    retry() {
      this.password1 = ''
      this.password2 = ''
      this.loading = false
      this.error = false
      this.sent = false
    }
  }
}
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Raleway:wght@600;700&family=Lato:wght@300;400;700&display=swap');

.raleway-font { font-family: 'Raleway', sans-serif !important; }
.lato-font { font-family: 'Lato', sans-serif !important; }

.auth-wrapper {
  min-height: 100vh;
  background-color: #F8FAFC;
  margin: -12px;
}

.min-vh-100 {
  min-height: 100vh !important;
}

.visual-pane {
  background-color: #1A1F2E;
  position: relative;
}

.visual-content { z-index: 1; max-width: 520px; }

.accent-text { color: #35BDC8 !important; }
.slate-text { color: #1A1F2E; }

.company-logo-login-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.04);
  border: 1px solid #F1F5F9;
}

.tracking-tight { letter-spacing: -1px !important; }
.tracking-widest { letter-spacing: 1.5px !important; }
.tracking-wider { letter-spacing: 0.8px !important; }
.lh-relaxed { line-height: 1.6 !important; }
.opacity-70 { opacity: 0.7; }

.form-pane { background-color: white; }

.auth-card-container {
  width: 100%;
  max-width: 520px;
}

.modern-input-field {
  ::v-deep .v-input__control {
    .v-input__slot {
      border-radius: 8px !important;
      background-color: #F8FAFC !important;
      border: 1px solid #E0E0E0 !important;
      box-shadow: none !important;
      transition: all 0.2s ease;
      
      fieldset { border: none; }
    }
    
    &.v-input--is-focused .v-input__slot {
      background-color: #FFFFFF !important;
      border-color: #35BDC8 !important;
      box-shadow: 0 0 0 3px rgba(53, 189, 200, 0.1) !important;
    }
  }
}

.h-52 { height: 52px !important; }
.rounded-md { border-radius: 8px !important; }

.login-btn-enterprise {
  transition: all 0.2s ease;
  box-shadow: none !important;
  &:hover {
    background-color: #2da1ac !important;
    transform: translateY(-1px);
  }
}

.border-top-light { border-top: 1px solid #E0E0E0; }

@media (max-width: 960px) {
  .auth-card-container {
    max-width: 100%;
  }
}
</style>